// The board: the offers standing in the lobby and the duels in flight.
//
// Read through the realm's lister contract (duel.gno, "listers"): OfferCount/
// DuelCount say how many, OfferIDs/DuelIDs page the IDs in ascending order,
// and the existing per-record reads fill in each row. Render("") is a page
// for humans and nothing here depends on it: a column or a separator can
// change there and this board does not notice.
//
// One composition, used by the live client and the rehearsal alike, so the
// two answer the same interface and cannot drift from each other. The
// interface a client must give readBoard:
//
//   offerCount() -> int      duelCount() -> int        escrowed() -> ugnot
//   offerIds(offset, limit) -> id[]   duelIds(offset, limit) -> id[]   (ascending)
//   offerOf(id) -> record | null      duelOf(id) -> record | null
//
// This is an N+1 on purpose: one read per record, capped at a single page
// (CONFIG.pageLimit, the realm's PageLimit). The board will hold single
// digits for a long time; a cache or a batching layer would be more code
// than the problem.
import { CONFIG } from '../config.js';

// The realm panics on a negative offset or limit and clamps a limit above
// PageLimit. Both listers, live and rehearsal, pass their arguments through
// here so a bad window is refused before it reaches the chain and a big one
// is trimmed to what the realm would answer anyway.
export function pageWindow(offset, limit) {
  const o = Number(offset), l = Number(limit);
  if (!Number.isInteger(o) || o < 0) throw new Error(`duel: negative offset ${offset}`);
  if (!Number.isInteger(l) || l < 0) throw new Error(`duel: negative limit ${limit}`);
  return { offset: o, limit: Math.min(l, CONFIG.pageLimit) };
}

// A record from the realm carries its rung; the stake is the ladder's word
// on that rung (duel.gno's ladder is a fixed array). `stakes` is the ladder
// as read at boot.
export const withStake = (rec, stakes) => (rec ? { ...rec, stake: stakes[rec.rung] } : rec);

export async function readBoard(client, stakes) {
  const [openOffers, inFlight, escrowed] = await Promise.all([client.offerCount(), client.duelCount(), client.escrowed()]);
  const [offerIds, duelIds] = await Promise.all([
    openOffers > 0 ? client.offerIds(0, CONFIG.pageLimit) : [],
    inFlight > 0 ? client.duelIds(0, CONFIG.pageLimit) : [],
  ]);
  const offers = [];
  for (const id of offerIds) { const o = await client.offerOf(id); if (o) offers.push(withStake(o, stakes)); }
  const duels = [];
  for (const id of duelIds) { const d = await client.duelOf(id); if (d) duels.push(withStake(d, stakes)); }
  return { offers, duels, escrowed, openOffers: Number(openOffers), inFlight: Number(inFlight) };
}
